// Demo mode for the eCombine dashboard.
//
// When demo mode is active the app runs entirely against in-memory fixtures
// (see ./demoData.ts) — no backend, no network, no real auth. This powers the
// "Launch Live Demo" experience linked from the marketing site so prospects can
// click through every role's dashboard with realistic dummy data.
//
// Activation paths:
//   - URL flag:  /?demo=1            (sets the flag, then the SPA boots in demo)
//   - Button:    enableDemo()        (from the login page "Explore the demo")
//
// State is persisted in localStorage so a full page reload keeps the user in the
// demo (and remembers which role they were exploring).

import type { User } from '../services/api'

const DEMO_FLAG_KEY = 'ecombine_demo'
const DEMO_ROLE_KEY = 'ecombine_demo_role'

export const DEMO_TOKEN = 'demo-token-ecombine'

export type DemoRole = User['role']

export interface DemoRoleOption {
  role: DemoRole
  label: string
  blurb: string
}

// Ordered for the role switcher — organizer first (richest landing experience).
export const DEMO_ROLES: DemoRoleOption[] = [
  {
    role: 'EVENT_ORGANIZER',
    label: 'Event Organizer',
    blurb: 'Run combines & monitor live results',
  },
  { role: 'COACH', label: 'Coach', blurb: 'Track your roster & training plans' },
  { role: 'SCOUT', label: 'Scout / Recruiter', blurb: 'Search & shortlist top prospects' },
  { role: 'ATHLETE', label: 'Athlete', blurb: 'See your results & recruiting profile' },
  { role: 'PARENT', label: 'Parent', blurb: 'Follow your athlete’s progress' },
  { role: 'ADMIN', label: 'District Admin', blurb: 'Oversee schools & programs' },
]

/** True when the app should serve fixtures instead of hitting the API. */
export function isDemoMode(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  // A ?demo=1 query param activates (and latches) demo mode on first load.
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('demo') === '1') {
      localStorage.setItem(DEMO_FLAG_KEY, '1')
    }
  } catch {
    // URL parsing can fail in exotic environments; fall back to the flag only.
  }

  return localStorage.getItem(DEMO_FLAG_KEY) === '1'
}

/** Turn on demo mode (optionally for a specific role) and persist a fake token. */
export function enableDemo(role: DemoRole = 'EVENT_ORGANIZER'): void {
  localStorage.setItem(DEMO_FLAG_KEY, '1')
  localStorage.setItem(DEMO_ROLE_KEY, role)
}

/** Exit demo mode and clear demo-specific state. */
export function disableDemo(): void {
  localStorage.removeItem(DEMO_FLAG_KEY)
  localStorage.removeItem(DEMO_ROLE_KEY)
}

export function getDemoRole(): DemoRole {
  const stored = localStorage.getItem(DEMO_ROLE_KEY) as DemoRole | null
  return stored || 'EVENT_ORGANIZER'
}

/** Switch the active demo role and reload so every query refetches fixtures. */
export function setDemoRole(role: DemoRole): void {
  localStorage.setItem(DEMO_ROLE_KEY, role)
  if (typeof window !== 'undefined') {
    // Target /login (a SPA path) rather than / so this works on hosts that serve
    // the marketing page at the root; the SPA re-boots demo with the new role and
    // client-redirects to the dashboard.
    window.location.assign('/login')
  }
}
